export type CategoryPlatformFieldRow = {
  id?: number | null
  field_code?: string | null
  api_code?: string | null
}

export const normalizeCategoryPlatformFieldsPayload = (
  payload: unknown
): CategoryPlatformFieldRow[] => {
  if (Array.isArray(payload)) return payload as CategoryPlatformFieldRow[]
  if (payload && typeof payload === 'object' && Array.isArray((payload as { results?: unknown }).results)) {
    return (payload as { results: CategoryPlatformFieldRow[] }).results
  }
  return []
}

const isCategoryFieldRow = (field: CategoryPlatformFieldRow): boolean => {
  const fieldCode = String(field.field_code ?? '').trim().toUpperCase()
  const apiCode = String(field.api_code ?? '').trim().toUpperCase()
  return fieldCode === 'CATEGORY' || apiCode === 'CATEGORY'
}

/** Pick CATEGORY platform field; prefer hint id when multiple exist. */
export const resolveCategoryPlatformFieldId = (
  fields: CategoryPlatformFieldRow[],
  hintPlatformFieldId?: number | null
): number | null => {
  if (!fields.length) {
    return hintPlatformFieldId != null ? Number(hintPlatformFieldId) : null
  }

  const categoryFields = fields.filter(isCategoryFieldRow)
  const candidates = categoryFields.length > 0 ? categoryFields : fields

  if (hintPlatformFieldId != null) {
    const hinted = candidates.find((field) => Number(field.id) === Number(hintPlatformFieldId))
    if (hinted?.id != null) return Number(hinted.id)
  }

  const firstWithId = candidates.find((field) => field.id != null)
  return firstWithId?.id != null ? Number(firstWithId.id) : null
}

export const resolvePlatformFieldIdFromCategoryPayload = (
  categoryData: unknown,
  categoryTemplate: unknown
): number | null => {
  const readFieldId = (value: unknown): number | null => {
    if (value == null || value === '') return null
    if (typeof value === 'object' && value !== null && 'id' in value) {
      const id = (value as { id?: unknown }).id
      return id != null && id !== '' ? Number(id) : null
    }
    return Number(value)
  }

  const cData = categoryData as Record<string, unknown> | null | undefined
  const catData = categoryTemplate as Record<string, unknown> | null | undefined

  return (
    readFieldId(cData?.platform_field) ??
    readFieldId(catData?.platform_field) ??
    null
  )
}

export const resolvePlatformFieldIdFromCategoryTreePath = (
  pathNodes: unknown[] | null | undefined
): number | null => {
  if (!pathNodes?.length) return null

  for (const node of pathNodes) {
    if (!node || typeof node !== 'object') continue
    const record = node as Record<string, unknown>
    const nestedItem =
      record.item && typeof record.item === 'object'
        ? (record.item as Record<string, unknown>)
        : null

    const fromNode = record.platform_field ?? nestedItem?.platform_field
    if (fromNode != null && fromNode !== '') {
      if (typeof fromNode === 'object' && fromNode !== null && 'id' in fromNode) {
        const id = (fromNode as { id?: unknown }).id
        if (id != null && id !== '') return Number(id)
      } else {
        return Number(fromNode)
      }
    }
  }

  return null
}
